import {
  Box,
  Button,
  Drawer,
  Grid,
  Stack,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { FC, ReactElement, useEffect, useMemo, useState } from "react";
import { format, subMonths, isValid, parseISO } from "date-fns";

import { DateRangeFilter, DynamicTable, HTSCodeWithTooltip } from "@/components";
import { useAppSelector } from "@/store";
import {
  useGetTariffAnalysisFindingsQuery,
  useGetHTSCodesForDropdownQuery,
  useCalculateDutiesQuery,
  useGetDutyColumnsQuery,
  useStartTariffAnalysisBuildMutation,
} from "@/services";
import { useGetCountriesForDropdownQuery, useGetManufacturersForDropdownQuery } from "@/services/report/report-api";
import { roles } from "@/roles";
import { LeftFilters, RightFilters, StyledHeadeButtons } from "./style";

type TariffAnalysisRow = {
  entrySummaryNumber?: string;
  entrySummaryLineNumber?: number;
  entryDate?: string;
  htsNumberFull?: string;
  htsDescription?: string;
  countryOfOriginCode?: string;
  manufacturerID?: string;
  manufacturerName?: string;
  goodsValue?: number;
  aceDuty: number;
  correctDuty: number;
  difference: number;
  finding: string;
  ch99?: string;
  ch99Code?: string;
  ch99Code2?: string;
  ch99CodeList?: string[];
  ch99DutyDict?: Record<string, number> | any;
};

const INITIAL_PAGE = 1;
const getDefaultDateRange = () => {
  const endDate = new Date();
  const startDate = subMonths(endDate, 6);
  return { startDate, endDate };
};

const formatHTS = (code?: string) => {
  if (!code) return "-";
  const digits = code.replace(/\D/g, "");
  if (digits.length === 10) {
    return `${digits.slice(0, 4)}.${digits.slice(4, 6)}.${digits.slice(6, 8)}.${digits.slice(8, 10)}`;
  }
  return code;
};

const currency0 = (n?: number) => {
  const value = Number(n || 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

const digitsOnly = (code?: string) => (code ? String(code).replace(/\D/g, "") : "");
const formatCh99Dots = (code8: string) =>
  code8.length === 8 ? `${code8.slice(0, 4)}.${code8.slice(4, 6)}.${code8.slice(6, 8)}` : code8;

const TariffAnalysisView: FC = (): ReactElement => {
  const { user } = useAppSelector((s) => s.auth);
  const { selectedClient } = useAppSelector((s) => (s as any).client);
  const { selectedTenant } = useAppSelector((s) => (s as any).tenantManagement);

  const isAdmin = user?.userRole === roles.TDADMIN;
  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const shouldSkip = useMemo(() => {
    // TD_ADMIN on TRADEDIVE_CLIENT tenant can view without selecting a client
    if (user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT") {
      return false;
    }
    return canSelectClients && !selectedClient;
  }, [canSelectClients, selectedClient, selectedTenant, user]);

  const clientIdForDropdowns = useMemo(() => {
    if (selectedClient?._id) return selectedClient._id;
    // TD_ADMIN on TRADEDIVE_CLIENT: use tenant ID as client context
    if (user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT") {
      return selectedTenant?._id;
    }
    return undefined;
  }, [selectedClient?._id, user?.userRole, selectedTenant?._id, selectedTenant?.tenantType]);

  const [page, setPage] = useState(INITIAL_PAGE);
  // DynamicTable page-size options are 10/20/30/50; keep default aligned to avoid MUI warning
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState<{ startDate?: Date; endDate?: Date }>(
    getDefaultDateRange()
  );

  const [manufacturerIds, setManufacturerIds] = useState<string[]>([]);
  const [originCodes, setOriginCodes] = useState<string[]>([]);
  const [htsCodes, setHtsCodes] = useState<string[]>([]);

  const [selectedRow, setSelectedRow] = useState<TariffAnalysisRow | null>(null);

  // When a finding is opened, run the duty calculator on that entry's HTS + COO + entry date
  const dutyCalcInput = useMemo(() => {
    if (!selectedRow?.htsNumberFull || !selectedRow?.countryOfOriginCode) return null;
    
    let effectiveDate: string | undefined = undefined;
    if (selectedRow.entryDate) {
      const date = typeof selectedRow.entryDate === "string" ? parseISO(selectedRow.entryDate) : new Date(selectedRow.entryDate as any);
      if (isValid(date)) {
        effectiveDate = format(date, "yyyy-MM-dd");
      }
    }
    
    return {
      htsCode: selectedRow.htsNumberFull,
      countryOfOrigin: selectedRow.countryOfOriginCode,
      effectiveDate,
    };
  }, [selectedRow]);

  const { data: dutyCalcData, isFetching: isDutyCalcFetching } = useCalculateDutiesQuery(
    dutyCalcInput as any,
    { skip: !dutyCalcInput }
  );

  const { data: dutyColumnsData } = useGetDutyColumnsQuery(
    { isActive: true, pageNo: 1, pageSize: 250 },
    { skip: !selectedRow } as any
  );

  const ch99Codes = useMemo(() => {
    if (!selectedRow) return [];
    const raw = [
      selectedRow.ch99Code,
      selectedRow.ch99Code2,
      ...(Array.isArray(selectedRow.ch99CodeList) ? selectedRow.ch99CodeList : []),
    ]
      .filter(Boolean)
      .map((c) => String(c))
      .filter((c) => c.toUpperCase() !== "N/A");

    const unique = Array.from(new Set(raw.map((c) => digitsOnly(c)).filter(Boolean)));
    return unique;
  }, [selectedRow]);

  const codeToDutyColumnName = useMemo(() => {
    const map = new Map<string, string>();
    const cols = (dutyColumnsData as any)?.data ?? [];
    (cols as any[]).forEach((col) => {
      const name = col?.name;
      const codes: any[] = Array.isArray(col?.ch99Codes) ? col.ch99Codes : [];
      codes.forEach((c) => {
        const code = digitsOnly(c);
        if (code && name) map.set(code, name);
      });
    });
    return map;
  }, [dutyColumnsData]);

  const dutyCalcColumnNameByCh99Code = useMemo(() => {
    const map = new Map<string, string>();
    const tariffs = (dutyCalcData as any)?.additionalTariffs ?? [];
    (tariffs as any[]).forEach((t) => {
      const name = String(t?.columnName || "");
      if (!name) return;
      ch99Codes.forEach((code) => {
        const dotted = formatCh99Dots(code);
        if (name.includes(code) || name.includes(dotted)) {
          map.set(code, name);
        }
      });
    });
    return map;
  }, [dutyCalcData, ch99Codes]);

  const parsedCh99DutyDict = useMemo(() => {
    const raw: any = selectedRow?.ch99DutyDict;
    if (!raw) return null;
    if (typeof raw === "object") return raw;
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    }
    return null;
  }, [selectedRow?.ch99DutyDict]);

  const totalCh99EffectiveRate = useMemo(() => {
    if (!selectedRow?.goodsValue || selectedRow.goodsValue <= 0 || ch99Codes.length === 0) return null;

    const dict: any = parsedCh99DutyDict;
    if (!dict || typeof dict !== "object") return null;

    let totalRate = 0;
    ch99Codes.forEach((code) => {
      const dottedKey = formatCh99Dots(code);
      const paidRaw = dict[code] ?? dict[dottedKey] ?? dict[String(code).padStart(8, "0")];
      if (paidRaw !== undefined && paidRaw !== null && paidRaw !== "") {
        const paid = Number(paidRaw);
        totalRate += paid / selectedRow.goodsValue!;
      }
    });

    return totalRate;
  }, [selectedRow, ch99Codes, parsedCh99DutyDict]);

  const filters = useMemo(() => {
    const f: Record<string, any> = {};
    if (manufacturerIds.length) f.manufacturerID = { value: manufacturerIds, operator: "in" };
    if (originCodes.length) f.countryOfOriginCode = { value: originCodes, operator: "in" };
    if (htsCodes.length) f.htsNumberFull = { value: htsCodes, operator: "in" };
    return f;
  }, [manufacturerIds, originCodes, htsCodes]);

  const queryInput = useMemo(() => {
    return {
      input: {
        pageNo: page,
        pageSize: limit,
        searchKeyword: search.trim() || undefined,
        filters: Object.keys(filters).length ? filters : undefined,
        ...(dateFilter.startDate && { dateFrom: format(dateFilter.startDate, "yyyy-MM-dd") }),
        ...(dateFilter.endDate && { dateTo: format(dateFilter.endDate, "yyyy-MM-dd") }),
        ...(user?.userRole &&
          [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN].includes(user.userRole) && {
            clientId: clientIdForDropdowns,
          }),
        ...(user?.userRole && [roles.TDADMIN].includes(user.userRole) && { tenantId: selectedTenant?._id }),
      },
    };
  }, [page, limit, search, filters, dateFilter, user, clientIdForDropdowns, selectedTenant]);

  const { data, isLoading, isFetching, refetch } = useGetTariffAnalysisFindingsQuery(queryInput, {
    skip: shouldSkip,
    refetchOnMountOrArgChange: true,
  } as any);

  const rows: TariffAnalysisRow[] = (data as any)?.tariffAnalysisFindings?.data ?? [];
  const totalCount: number = (data as any)?.tariffAnalysisFindings?.meta?.countDocs ?? 0;
  const buildStatus = (data as any)?.tariffAnalysisFindings?.build;

  // Rebuild mutation
  const [startBuild, { isLoading: isStartingBuild }] = useStartTariffAnalysisBuildMutation();

  const handleRebuild = async () => {
    try {
      await startBuild({ input: queryInput.input });
      // Polling useEffect will handle progress updates
      refetch();
    } catch (error) {
      console.error("Failed to start tariff analysis rebuild:", error);
    }
  };

  const isBuildInProgress = buildStatus?.status === "inprocess";

  // Poll for build progress updates while a build is in progress
  useEffect(() => {
    if (!isBuildInProgress) return;

    const interval = setInterval(() => {
      refetch();
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(interval);
  }, [isBuildInProgress, refetch]);

  const { data: htsOptions = [] } = useGetHTSCodesForDropdownQuery(
    {
      clientId: clientIdForDropdowns,
      ...(isAdmin && { tenantId: selectedTenant?._id }),
      includeChapter99: false,
    },
    { skip: shouldSkip }
  );

  const { data: countryOptions = [] } = useGetCountriesForDropdownQuery(
    {
      clientId: clientIdForDropdowns || "",
      dateFrom: dateFilter.startDate ? format(dateFilter.startDate, "yyyy-MM-dd") : undefined,
      dateTo: dateFilter.endDate ? format(dateFilter.endDate, "yyyy-MM-dd") : undefined,
    },
    { skip: shouldSkip }
  );

  const { data: manufacturerOptions = [] } = useGetManufacturersForDropdownQuery(
    {
      dashboardVisibility: {
        clientId: clientIdForDropdowns,
        ...(isAdmin && { tenantId: selectedTenant?._id }),
      },
    },
    { skip: shouldSkip }
  );

  const htsOnlyOptions = useMemo(() => {
    return (htsOptions as any[]).filter((x) => x.type === "hts");
  }, [htsOptions]);

  const columns = useMemo(() => {
    return [
      {
        key: ["entrySummaryNumber"],
        name: "Entry / Product",
        align: "left",
        minWidth: "360px",
        component: (item: TariffAnalysisRow) => {
          const entryLine = `${item.entrySummaryNumber || "-"}-${item.entrySummaryLineNumber ?? "-"}`;
          const hts = formatHTS(item.htsNumberFull);
          const desc = item.htsDescription ? ` - ${item.htsDescription}` : "";
          return (
            <Stack spacing={0.25}>
              <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{entryLine}</Typography>
              <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                {hts}
                {desc}
              </Typography>
            </Stack>
          );
        },
      },
      {
        key: ["countryOfOriginCode"],
        name: "Origin",
        align: "center",
        minWidth: "90px",
        component: (item: TariffAnalysisRow) => <Box sx={{ whiteSpace: "nowrap" }}>{item.countryOfOriginCode || "-"}</Box>,
      },
      {
        key: ["aceDuty"],
        name: "ACE Duty",
        align: "right",
        minWidth: "120px",
        component: (item: TariffAnalysisRow) => <Box sx={{ whiteSpace: "nowrap" }}>{currency0(item.aceDuty)}</Box>,
      },
      {
        key: ["correctDuty"],
        name: "Correct Duty",
        align: "right",
        minWidth: "120px",
        component: (item: TariffAnalysisRow) => <Box sx={{ whiteSpace: "nowrap" }}>{currency0(item.correctDuty)}</Box>,
      },
      {
        key: ["difference"],
        name: "Difference",
        align: "right",
        minWidth: "120px",
        component: (item: TariffAnalysisRow) => {
          const diff = Number(item.difference || 0);
          const sign = diff > 0 ? "+" : diff < 0 ? "-" : "";
          const color = diff > 0 ? "success.main" : diff < 0 ? "error.main" : "text.primary";
          return (
            <Box sx={{ whiteSpace: "nowrap", fontWeight: 700, color }}>
              {sign}
              {currency0(Math.abs(diff))}
            </Box>
          );
        },
      },
      {
        key: ["finding"],
        name: "Finding",
        align: "left",
        minWidth: "160px",
        component: (item: TariffAnalysisRow) => <Box sx={{ whiteSpace: "nowrap" }}>{item.finding || "-"}</Box>,
      },
      {
        key: ["view"],
        name: "",
        align: "right",
        minWidth: "80px",
        component: (item: TariffAnalysisRow) => (
          <Button size="small" onClick={() => setSelectedRow(item)}>
            View
          </Button>
        ),
      },
    ] as any[];
  }, []);

  const handleClear = () => {
    setManufacturerIds([]);
    setOriginCodes([]);
    setHtsCodes([]);
    setSearch("");
    setDateFilter(getDefaultDateRange());
    setPage(INITIAL_PAGE);
  };

  // No build button and no client-side build trigger. Backend auto-builds when stale/missing.

  if (shouldSkip && canSelectClients) {
    return (
      <Stack sx={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <Typography sx={{ fontSize: 16, fontWeight: 500, color: "#64748B" }}>
          Please select a client to view tariff analysis
        </Typography>
      </Stack>
    );
  }

  return (
    <>
      <Stack sx={{ flex: 1 }}>
        <StyledHeadeButtons container spacing={1.5}>
          <Grid item xs={12} md={12}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={1.5}>
              <LeftFilters>
                <DateRangeFilter
                  value={dateFilter}
                  onSelectDate={(range: { startDate?: Date; endDate?: Date }) => {
                    setDateFilter(range);
                    setPage(INITIAL_PAGE);
                  }}
                />
                <Autocomplete
                  multiple
                  size="small"
                  sx={{ width: 260 }}
                  options={manufacturerOptions}
                  getOptionLabel={(o: any) => o.label || o.value}
                  value={manufacturerOptions.filter((o: any) => manufacturerIds.includes(o.value))}
                  onChange={(_, value: any[]) => {
                    setManufacturerIds(value.map((v) => v.value));
                    setPage(INITIAL_PAGE);
                  }}
                  renderInput={(params) => <TextField {...params} label="Manufacturers" />}
                />
                <Autocomplete
                  multiple
                  size="small"
                  sx={{ width: 200 }}
                  options={countryOptions}
                  getOptionLabel={(o: any) => o.label || o.value}
                  value={countryOptions.filter((o: any) => originCodes.includes(o.value))}
                  onChange={(_, value: any[]) => {
                    setOriginCodes(value.map((v) => v.value));
                    setPage(INITIAL_PAGE);
                  }}
                  renderInput={(params) => <TextField {...params} label="Origin (COO)" />}
                />
                <Autocomplete
                  multiple
                  size="small"
                  sx={{ width: 280 }}
                  options={htsOnlyOptions}
                  getOptionLabel={(o: any) => o.label || o.value}
                  value={htsOnlyOptions.filter((o: any) => htsCodes.includes(o.value))}
                  onChange={(_, value: any[]) => {
                    setHtsCodes(value.map((v) => v.value));
                    setPage(INITIAL_PAGE);
                  }}
                  renderInput={(params) => <TextField {...params} label="HTS" />}
                />
              </LeftFilters>

              <RightFilters>
                <TextField
                  size="small"
                  label="Search entry summaries"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(INITIAL_PAGE);
                  }}
                  sx={{ width: 260 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleRebuild}
                  disabled={isStartingBuild || isBuildInProgress || shouldSkip}
                  startIcon={
                    isStartingBuild || isBuildInProgress ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                >
                  {isBuildInProgress
                    ? `Building... ${Math.round(buildStatus?.progress || 0)}%`
                    : isStartingBuild
                    ? "Starting..."
                    : "Rebuild"}
                </Button>
                <Button variant="outlined" size="small" onClick={handleClear}>
                  Clear
                </Button>
              </RightFilters>
            </Stack>
          </Grid>
        </StyledHeadeButtons>

        <DynamicTable
          data={rows as any[]}
          columns={columns as any}
          isLoading={isLoading || isFetching}
          handleLimitChange={(newLimit: number) => {
            setLimit(newLimit);
            setPage(INITIAL_PAGE);
          }}
          handlePageChange={setPage}
          limit={limit}
          page={page}
          total={totalCount}
          hidePagination={false}
          isFullHeight
          stickyHeader
          hideScrollBars
          sx={{ flex: 1 }}
        />
      </Stack>

      <Drawer anchor="right" open={!!selectedRow} onClose={() => setSelectedRow(null)}>
        <Box sx={{ width: 420, p: 2 }}>
          <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Tariff Analysis – Details</Typography>
          {selectedRow && (
            <Stack spacing={1}>
              <Typography sx={{ fontWeight: 700 }}>
                {selectedRow.entrySummaryNumber}-{selectedRow.entrySummaryLineNumber ?? "-"}
              </Typography>
              <Typography sx={{ color: "text.secondary" }}>
                {formatHTS(selectedRow.htsNumberFull)}{selectedRow.htsDescription ? ` - ${selectedRow.htsDescription}` : ""}
              </Typography>
              <Typography>
                Entry Date:{" "}
                {(() => {
                  if (!selectedRow.entryDate) return "-";
                  const date = typeof selectedRow.entryDate === "string" ? parseISO(selectedRow.entryDate) : new Date(selectedRow.entryDate as any);
                  return isValid(date) ? format(date, "yyyy-MM-dd") : "-";
                })()}
              </Typography>
              <Typography>Origin: {selectedRow.countryOfOriginCode || "-"}</Typography>
              <Typography>Goods Value: {currency0(selectedRow.goodsValue)}</Typography>
              <Typography>ACE Duty: {currency0(selectedRow.aceDuty)}</Typography>
              <Typography>Correct Duty: {currency0(selectedRow.correctDuty)}</Typography>
              <Typography>Difference: {currency0(selectedRow.difference)}</Typography>
              <Typography sx={{ fontWeight: 700 }}>Finding: {selectedRow.finding}</Typography>

              <Box sx={{ mt: 1.5 }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Expected Duty Columns (Duty Calculator)</Typography>
                {isDutyCalcFetching ? (
                  <Typography sx={{ color: "text.secondary" }}>Loading duty columns…</Typography>
                ) : dutyCalcData ? (
                  <Stack spacing={0.5}>
                    <Typography sx={{ color: "text.secondary" }}>
                      Total expected duty rate: {(((dutyCalcData as any).totalDutyRate ?? 0) * 100).toFixed(2)}%
                    </Typography>
                    {Array.isArray((dutyCalcData as any).additionalTariffs) && (dutyCalcData as any).additionalTariffs.length > 0 ? (
                      (dutyCalcData as any).additionalTariffs
                        .slice()
                        .sort((a: any, b: any) => (b.rate ?? 0) - (a.rate ?? 0))
                        .map((t: any) => (
                          <Stack
                            key={`${t.columnId}-${t.columnName}`}
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: 2 }}
                          >
                            <Typography sx={{ fontSize: 13 }}>{t.columnName || "Duty Column"}</Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                              {(((t.rate ?? 0) as number) * 100).toFixed(2)}%
                            </Typography>
                          </Stack>
                        ))
                    ) : (
                      <Typography sx={{ color: "text.secondary" }}>No duty columns returned.</Typography>
                    )}
                  </Stack>
                ) : (
                  <Typography sx={{ color: "text.secondary" }}>No duty calculator result.</Typography>
                )}
              </Box>

              <Box sx={{ mt: 1.5 }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Chapter 99 (from ACE data)</Typography>
                {totalCh99EffectiveRate !== null && (
                  <Typography sx={{ color: "text.secondary", mb: 1 }}>
                    Total effective rate: {(totalCh99EffectiveRate * 100).toFixed(2)}%
                  </Typography>
                )}
                {ch99Codes.length === 0 ? (
                  <Typography>Codes: -</Typography>
                ) : (
                  <Stack spacing={0.75}>
                    {ch99Codes.map((code) => {
                      const colName =
                        codeToDutyColumnName.get(code) ||
                        dutyCalcColumnNameByCh99Code.get(code);

                      const dict: any = parsedCh99DutyDict;
                      const dottedKey = formatCh99Dots(code);
                      const paidRaw =
                        dict?.[code] ??
                        dict?.[dottedKey] ??
                        dict?.[String(code).padStart(8, "0")];
                      const hasDict = !!dict && typeof dict === "object";
                      const hasPaid =
                        paidRaw !== undefined && paidRaw !== null && paidRaw !== "";
                      const paid = hasPaid ? Number(paidRaw) : null;

                      // Derive rate from paid / goodsValue
                      const derivedRate = (paid !== null && selectedRow.goodsValue && selectedRow.goodsValue > 0)
                        ? paid / selectedRow.goodsValue
                        : null;

                      return (
                        <Stack
                          key={code}
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ gap: 2 }}
                        >
                          <HTSCodeWithTooltip htsCode={code}>
                            <Typography sx={{ fontFamily: "monospace", cursor: "help" }}>{code}</Typography>
                          </HTSCodeWithTooltip>
                          <Stack spacing={0} alignItems="flex-end">
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                              {colName ? `${colName}` : "Unmapped"}
                            </Typography>
                            <Typography sx={{ fontSize: 12 }}>
                              Paid:{" "}
                              {hasDict ? (paid === null ? "N/A" : currency0(paid)) : "N/A"}{" "}
                              {derivedRate !== null ? `• Rate: ${(derivedRate * 100).toFixed(2)}%` : ""}
                            </Typography>
                          </Stack>
                        </Stack>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default TariffAnalysisView;


